import dayjs from "dayjs";

export const MAX_DASHBOARD_PERIOD_DAYS = 366;

function inclusiveSpanDays(start, end) {
  if (!start?.isValid?.() || !end?.isValid?.()) return 1;
  return Math.max(1, end.startOf("day").diff(start.startOf("day"), "day") + 1);
}

/**
 * Garante intervalo válido: início ≤ fim, fim não no futuro, span máximo respeitado.
 * @param {import("dayjs").Dayjs | null | undefined} start
 * @param {import("dayjs").Dayjs | null | undefined} end
 * @param {{ changed?: "start" | "end", priorSpanDays?: number }} [options]
 *   Campo alterado pelo usuário; priorSpanDays preserva a duração do período ao empurrar a outra data.
 */
export function normalizeDashboardDateRange(start, end, options = {}) {
  const { changed } = options;
  const priorSpan = Math.max(1, Number(options.priorSpanDays) || 1);
  const today = dayjs().endOf("day");

  let s = start?.isValid?.() ? start.startOf("day") : null;
  let e = end?.isValid?.() ? end.endOf("day") : null;
  let adjusted = false;

  if (!s && e) s = e.startOf("day");
  if (!e && s) e = s.endOf("day");
  if (!s || !e) return { start: s, end: e, adjusted };

  if (s.isAfter(e, "day")) {
    adjusted = true;
    if (changed === "start") {
      e = s.add(priorSpan - 1, "day").endOf("day");
      if (e.isAfter(today)) e = today;
      if (e.isBefore(s, "day")) e = s.endOf("day");
    } else if (changed === "end") {
      s = e.subtract(priorSpan - 1, "day").startOf("day");
    } else {
      e = s.add(priorSpan - 1, "day").endOf("day");
      if (e.isAfter(today)) e = today;
      if (e.isBefore(s, "day")) e = s.endOf("day");
    }
  }

  if (e.isAfter(today)) {
    adjusted = true;
    e = today;
    if (s.isAfter(e, "day")) s = e.startOf("day");
  }

  const spanDays = e.startOf("day").diff(s.startOf("day"), "day") + 1;
  if (spanDays > MAX_DASHBOARD_PERIOD_DAYS) {
    adjusted = true;
    if (changed === "end") {
      s = e.subtract(MAX_DASHBOARD_PERIOD_DAYS - 1, "day").startOf("day");
    } else {
      e = s.add(MAX_DASHBOARD_PERIOD_DAYS - 1, "day").endOf("day");
      if (e.isAfter(today)) e = today;
    }
  }

  return { start: s, end: e, adjusted };
}

export function getInitialDashboardDateRange(readDate) {
  const read = typeof readDate === "function" ? readDate : () => null;
  const startRaw = read("start");
  const endRaw = read("end");

  const start = startRaw ? dayjs(startRaw, "YYYY-MM-DD", true) : null;
  const end = endRaw ? dayjs(endRaw, "YYYY-MM-DD", true) : null;

  const fallbackStart = dayjs().subtract(6, "day").startOf("day");
  const fallbackEnd = dayjs().endOf("day");

  const normalized = normalizeDashboardDateRange(
    start?.isValid() ? start : fallbackStart,
    end?.isValid() ? end : fallbackEnd
  );

  return normalized;
}

export function getDashboardInclusiveSpanDays(start, end) {
  return inclusiveSpanDays(start, end);
}

/** Dias entre start/end (YYYY-MM-DD), inclusive. */
export function dashboardSpanDays(start, end) {
  if (!start || !end) return 0;
  const s = dayjs(start, "YYYY-MM-DD", true);
  const e = dayjs(end, "YYYY-MM-DD", true);
  if (!s.isValid() || !e.isValid()) return 0;
  return e.startOf("day").diff(s.startOf("day"), "day") + 1;
}

/** Períodos longos se beneficiam de preview em paralelo ao relatório completo. */
export function shouldPrefetchDashboardPreview(start, end, minSpanDays = 14) {
  return dashboardSpanDays(start, end) > minSpanDays;
}

/** Espelha a política de async do partner dashboard — preview deve existir quando o job em background é esperado. */
const CONSOLIDATED_PREVIEW_MAX_WORKSPACES = 8;

export function shouldPrefetchPartnerDashboardPreview(start, end, workspaceCount = 0) {
  const count = Math.max(0, Number(workspaceCount) || 0);
  if (count > CONSOLIDATED_PREVIEW_MAX_WORKSPACES) return false;

  const spanDays = dashboardSpanDays(start, end);

  if (count >= 10) return true;
  if (count >= 5 && spanDays > 14) return true;
  if (count >= 3 && spanDays > 45) return true;
  if (count >= 2 && spanDays > 120) return true;
  if (count >= 4 && spanDays > 90) return true;
  if (count >= 6) return true;
  if (count >= 3 && spanDays > 31) return true;
  return shouldPrefetchDashboardPreview(start, end);
}
